import React, { useState } from 'react';
import { MdNotificationsActive } from 'react-icons/md';
import apiClient from '../../services/api';
import { formatPrice } from 'utils/formatPrice';
import { t } from 'i18n';

const PriceAlert = ({ productId, currentPrice, productName, compact = false }) => {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [targetPrice, setTargetPrice] = useState(currentPrice - 1);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const handleSubscribe = async () => {
        if (!email) {
            setStatus({ type: 'error', message: t('priceAlert.needEmail') });
            return;
        }
        const parsedTargetPrice = Number.parseFloat(targetPrice);
        if (!Number.isFinite(parsedTargetPrice) || parsedTargetPrice <= 0) {
            setStatus({ type: 'error', message: t('priceAlert.needPrice') });
            return;
        }

        setLoading(true);
        try {
            await apiClient.post('/alerts/subscribe', {
                product_id: productId,
                email,
                // Compatibility fallback for stale backend instances that still require phone_number.
                // New backend ignores this extra field.
                phone_number: email,
                target_price: parsedTargetPrice
            });
            setStatus({ type: 'success', message: t('priceAlert.created') });
            setTimeout(() => {
                setOpen(false);
                setStatus({ type: '', message: '' });
            }, 2000);
        } catch (error) {
            const detail = error?.response?.data?.detail;
            const message =
                (Array.isArray(detail) && detail[0]?.msg) ||
                (typeof detail === 'string' && detail) ||
                t('productPage.wrong');
            setStatus({ type: 'error', message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {compact ? (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text hover:bg-surface-2"
                    data-testid="price-alert-button"
                >
                    <MdNotificationsActive className="h-4 w-4 shrink-0" aria-hidden />
                    {t('priceAlert.button')}
                </button>
            ) : (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-primary py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-50"
            >
                <MdNotificationsActive className="h-5 w-5 shrink-0" aria-hidden />
                {t('priceAlert.button')}
            </button>
            )}

            {open && (
                <div
                    className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
                    role="presentation"
                    onClick={(e) => e.target === e.currentTarget && setOpen(false)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="price-alert-title"
                        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b border-slate-100 px-6 py-4">
                            <h2 id="price-alert-title" className="flex items-center gap-2 text-lg font-bold text-slate-900">
                                <MdNotificationsActive className="h-6 w-6 text-amber-700" aria-hidden />
                                {t('priceAlert.title')}
                            </h2>
                        </div>
                        <div className="px-6 py-4">
                            <p className="mb-4 text-sm text-slate-600">
                                {t('priceAlert.body')}
                            </p>

                            <div className="flex flex-col gap-4">
                                <div>
                                    <label htmlFor="price-alert-email" className="mb-1 block text-sm font-medium text-slate-700">
                                        {t('account.email')}
                                    </label>
                                    <input
                                        id="price-alert-email"
                                        type="email"
                                        placeholder={t('priceAlert.emailPlaceholder')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="price-alert-target" className="mb-1 block text-sm font-medium text-slate-700">
                                        {t('priceAlert.target')}
                                    </label>
                                    <input
                                        id="price-alert-target"
                                        type="number"
                                        value={targetPrice}
                                        onChange={(e) => setTargetPrice(e.target.value)}
                                        placeholder="e.g. 5000"
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                    />
                                    <p className="mt-1 text-xs text-slate-500">{t('priceAlert.current', { amount: formatPrice(currentPrice) })}</p>
                                </div>
                            </div>

                            {status.message && (
                                <div
                                    className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                                        status.type === 'success'
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                            : 'border-red-200 bg-red-50 text-red-800'
                                    }`}
                                    role="alert"
                                >
                                    {status.message}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSubscribe}
                                disabled={loading}
                                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? t('newsletter.subscribing') : t('product.subscribe')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PriceAlert;
